import { getEmbeddedLaunchDestinationFullPathOrNull } from '../../authentication/embeddedLaunchDestination';
import {
	getEmbeddedOverlayLegacyAppointmentPortraitPath,
	getEmbeddedPatientPortraitPath,
	type getLegacyAppointmentPortraitPath,
	type getPatientPortraitPath,
} from '../../react-router/routes/PatientPortraitRoutes/PatientPortraitRoutes.utils';
import { getOrCreateApiGateway } from '../../server/apiGateway';
import { type CognitoNavinaJwt } from '../../stores/authStore';
import type { GetEpicTokenAndAppContextResponse } from '../../types';
import { isCurrentScreenLayoutEmbeddedOrOverlay } from '../../utilities/screenLayout';
import type { Auth0JwtToken, UserMetadata } from '../../utils/auth';
import { addCurrentTimeUnixToLocalStorage, getParamFromUrl } from '../../utils/common';
import {
	AUTH_0_CONNECTION_NAME,
	AUTH_0_TOKEN,
	EPIC_LOGIN_DOC_REDIRECT_TIME,
	GET_EPIC_TOKEN_AND_APP_CONTEXT_REQUEST_TIME,
	GET_EPIC_TOKEN_AND_APP_CONTEXT_RESPONSE_TIME,
	REDIRECTING_TO_PATIENT_TIME,
	REDIRECTING_TO_SUMMARY_TIME,
	SMART_ON_FHIR_LUNCH_TIME,
	USER_METADATA,
} from '../../utils/consts';
import { EpicFhirCache } from '../../utils/epicFhirCache';
import { getOrCreateLogger } from '../../utils/logger';
import { EPIC_CLIENT_AUD, EPIC_CLIENT_AUTH_URL, EPIC_CLIENT_TOKEN_ENDPOINT, EPIC_CLIENT_LAUNCH_ISS } from './consts';
import type { GetAppContextDataInputType } from './types';
import type { NavinaAppointmentId } from '@navina/api-types';
import type { AxiosResponse } from 'axios';
import JwtDecode from 'jwt-decode';
import type { NavigateFunction } from 'react-router';

const logger = getOrCreateLogger();

const getEpicURLFromWellKnown = async (
	iss: string,
): Promise<{
	authServerUrl: string;
	tokenEndpoint: string;
	aud: string;
}> => {
	console.log(iss);
	const wellKnownUrl = `${iss}/.well-known/smart-configuration`;
	const wellKnownUrlResponse = await fetch(wellKnownUrl);
	const wellKnownConfiguration = await wellKnownUrlResponse.text();
	const wellKnownConfigurationJson = JSON.parse(wellKnownConfiguration);
	const authServerUrl = wellKnownConfigurationJson.authorization_endpoint;
	const tokenEndpoint = wellKnownConfigurationJson.token_endpoint;
	console.log('url response', { authServerUrl, tokenEndpoint, aud: iss });
	return { authServerUrl, tokenEndpoint, aud: iss };
};

const storeEpicClientURLInLocalStorage = (input: {
	authServerUrl: string;
	tokenEndpoint: string;
	aud: string;
}): void => {
	localStorage.setItem(EPIC_CLIENT_TOKEN_ENDPOINT, input.tokenEndpoint);
	localStorage.setItem(EPIC_CLIENT_AUD, input.aud);
	localStorage.setItem(EPIC_CLIENT_AUTH_URL, input.authServerUrl);
};

const getAndAddEpicClientURLToLocalStorage = async (iss: string) => {
	console.log('getting token endpoint from well known because it is not in local storage');
	const { authServerUrl, tokenEndpoint, aud } = await getEpicURLFromWellKnown(iss);
	return { tokenEndpoint, aud, authServerUrl };
};

const handleMissingEpicAuthParams = async () => {
	const iss = localStorage.getItem(EPIC_CLIENT_LAUNCH_ISS);
	if (!iss) {
		logger.error('iss not found in local storage cannot get token endpoint from well known');
		throw new Error('internal error, cannot get token endpoint from well known please try again later');
	}
	const epicUrls = await getAndAddEpicClientURLToLocalStorage(iss);
	storeEpicClientURLInLocalStorage(epicUrls);
	return epicUrls;
};

function navigateToRedirectUrl(navigate: NavigateFunction, redirectUrl: string): void {
	const isOverlayOrEmbeddedNavina = isCurrentScreenLayoutEmbeddedOrOverlay();
	console.log('navigateToRedirectUrl', { redirectUrl, isOverlayOrEmbeddedNavina });

	if (isOverlayOrEmbeddedNavina) {
		navigate(redirectUrl);
		return;
	}

	globalThis.location.href = redirectUrl;
}

function updateEpicEncounterCacheIfPresent(
	summaryId: NavinaAppointmentId | undefined,
	emrEncounterFhirId: string | undefined,
): void {
	if (!summaryId) {
		return;
	}
	if (!emrEncounterFhirId) {
		return;
	}
	EpicFhirCache.removeAllAndUpdate({ summaryId, data: { emrEncounterFhirId } });
}

const handleTokenAndAppContextResponse = (
	appContextData: AxiosResponse<GetEpicTokenAndAppContextResponse>,
	navigate: NavigateFunction,
): true | undefined => {
	logger.trace('appContextData', appContextData);
	const { summaryId: sid, patientId: nid } = appContextData.data;
	const document_id = appContextData.data.documentId;
	const emrEncounterFhirId: string | undefined = appContextData?.data?.emrEncounterFhirId;
	const { search } = globalThis.location;
	const embeddedLaunchDestinationPath = getEmbeddedLaunchDestinationFullPathOrNull(search);
	if (embeddedLaunchDestinationPath !== null) {
		updateEpicEncounterCacheIfPresent(sid, emrEncounterFhirId);
		logger.trace('redirecting to embedded launch destination page', { embeddedLaunchDestinationPath });
		navigateToRedirectUrl(navigate, embeddedLaunchDestinationPath);
		return true;
	}

	if (sid && !document_id) {
		updateEpicEncounterCacheIfPresent(sid, emrEncounterFhirId);
		logger.trace('redirecting to summary page');
		addCurrentTimeUnixToLocalStorage(REDIRECTING_TO_SUMMARY_TIME);
		navigateToRedirectUrl(navigate, getRedirectUrlPathToPortraitPageOrThrow({ sid, nid }));
		return true;
	}

	if (nid) {
		logger.trace('redirecting to patient page');
		addCurrentTimeUnixToLocalStorage(REDIRECTING_TO_PATIENT_TIME);
		navigateToRedirectUrl(navigate, getRedirectUrlPathToPortraitPageOrThrow({ sid: undefined, nid }));
		return true;
	}

	if (document_id) {
		addCurrentTimeUnixToLocalStorage(EPIC_LOGIN_DOC_REDIRECT_TIME);
		logger.trace('redirecting to document page');
		navigate(`/doc/${document_id}`);
		return true;
	}
};

interface GetRedirectUrlPathToPortraitPageParams {
	readonly sid?: string;
	readonly nid?: string;
}

type GetRedirectUrlPathToPortraitPageReturnType =
	| ReturnType<typeof getLegacyAppointmentPortraitPath>
	| ReturnType<typeof getPatientPortraitPath>
	| ReturnType<typeof getEmbeddedOverlayLegacyAppointmentPortraitPath>
	| ReturnType<typeof getEmbeddedPatientPortraitPath>;

// Possible scenarios:
// OLD NAVINA - navinaAppointmentId exists
// NEW NAVINA 1 - navinaPatientId exists
// NEW NAVINA 2 - both navinaPatientId and navinaAppointmentId exist
function getRedirectUrlPathToPortraitPageOrThrow({
	sid: navinaAppointmentId,
	nid: navinaPatientId,
}: GetRedirectUrlPathToPortraitPageParams): GetRedirectUrlPathToPortraitPageReturnType {
	if (navinaPatientId) {
		return getEmbeddedPatientPortraitPath({ navinaPatientId, navinaAppointmentId });
	}

	if (navinaAppointmentId) {
		return getEmbeddedOverlayLegacyAppointmentPortraitPath({ navinaAppointmentId });
	}

	throw new Error('Both Patient ID and Appointment ID are missing');
}

const getAppContextDataInput = async (): Promise<GetAppContextDataInputType> => {
	const urlParams = new URLSearchParams(globalThis.location.search);
	const code = urlParams.get('code');
	const userMetadata: UserMetadata = JSON.parse(localStorage.getItem(USER_METADATA) || '{}');
	const connectionName = localStorage.getItem(AUTH_0_CONNECTION_NAME);
	let tokenUrlEndpoint = localStorage.getItem(EPIC_CLIENT_TOKEN_ENDPOINT);

	if (!tokenUrlEndpoint) {
		const { tokenEndpoint } = await handleMissingEpicAuthParams();
		tokenUrlEndpoint = tokenEndpoint;
	}

	const maybeAuth0TokenString = localStorage.getItem(AUTH_0_TOKEN);
	if (!maybeAuth0TokenString) {
		return {
			code,
			connectionName,
			dataSourceId: userMetadata.dataSourceId || null,
			redirectUri: globalThis.location.origin + globalThis.location.pathname,
			tokenUrl: tokenUrlEndpoint,
		};
	}

	const auth0Token = JwtDecode<Auth0JwtToken>(maybeAuth0TokenString);
	const dataSourceIds = auth0Token['https://navina.ai/dataSourceIds'];
	const dataSourceId =
		userMetadata.dataSourceId ||
		(Array.isArray(dataSourceIds) && dataSourceIds.length > 0 ? dataSourceIds : null) ||
		auth0Token['https://navina.ai/dataSourceId'] ||
		null;

	return {
		code,
		connectionName,
		dataSourceId: dataSourceId,
		redirectUri: globalThis.location.origin + globalThis.location.pathname,
		tokenUrl: tokenUrlEndpoint,
	};
};

const handleEpicLoginAfterEpicAuth = async (navigate: NavigateFunction): Promise<void> => {
	const appContextDataInput = await getAppContextDataInput();
	logger.trace('appContextData input', appContextDataInput);

	// executing post request to get the token
	addCurrentTimeUnixToLocalStorage(GET_EPIC_TOKEN_AND_APP_CONTEXT_REQUEST_TIME);
	// @ts-expect-error - Legacy code, nullability not yet enforced
	const appContextData = await getOrCreateApiGateway().getEpicTokenAndAppContext(appContextDataInput);
	addCurrentTimeUnixToLocalStorage(GET_EPIC_TOKEN_AND_APP_CONTEXT_RESPONSE_TIME);

	// @ts-expect-error - Legacy code, nullability not yet enforced
	const redirectedFromFunction = handleTokenAndAppContextResponse(appContextData, navigate);

	if (!redirectedFromFunction) {
		logger.error('no document id or summary id found redirecting to a unknown sid');
		// clearing local storage to block user from going back to wrong summary or document or schedule page
		localStorage.clear(); // to be logged in to auth0 next time
		navigate('/login');
	}
};

export const isTokenAboutToExpire = (authToken: string | null): boolean => {
	if (!authToken) {
		return true;
	}

	const exp = JwtDecode<CognitoNavinaJwt>(authToken).exp;
	const tokenExpirationInMillisecondsMinusOneHour = exp * 1_000 - 60 * 60 * 1_000;
	return tokenExpirationInMillisecondsMinusOneHour - Date.now() < 0;
};

const getCodeParamFromUrl = (): string | null => getParamFromUrl('code');

export const EpicAuthLoginFlow = (navigate: NavigateFunction): void => {
	if (getCodeParamFromUrl()) {
		logger.trace('getting token from epic');
		// Epic Redirected back flow
		handleEpicLoginAfterEpicAuth(navigate);
	}
};

export const isPartOfEPICLoginFlow = (): boolean => Boolean(localStorage.getItem(SMART_ON_FHIR_LUNCH_TIME));
